import React from 'react';
import CSelect, { Error } from 'src/components/form/Select';
import { ActionMeta, Props } from 'react-select';
import _ from 'lodash';
import { MaturityChoice, MaturityOption, maturityClearOption, maturityLevelOptions } from 'src/api/types';
import { MaturityLevel } from 'src/graphql/_generated';

/** A level row. What a caller that has NOT opted into clearing can ever be handed. */
export interface MaturityLevelOption {
  label: string;
  value: MaturityLevel;
}

interface BaseProps extends Omit<Props, 'value' | 'options' | 'onChange'> {
  onBlur?: () => void;
  error?: boolean;
  helperText?: string | false;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * CRPD-7274 — `allowClear` puts a "Clear" row above the levels, so a scored control can be
 * returned to unscored.
 *
 * Opt-in rather than always on, and **discriminated** rather than a plain boolean: turning it on
 * is what makes `MATURITY_CLEAR` reachable, so it is also what widens what `onChange` must handle.
 * Left off, callers keep the level-only handler they already have and cannot be passed a sentinel
 * they never render.
 *
 * Deliberately NOT react-select's own `isClearable`. That renders a cross in the control, which
 * reads as "discard what I typed" — in the bulk bar that is a real and *different* action from
 * "write a null to every selected control", and in an inline table cell the cross competes with
 * the edit pencil for a 24px target. A row in the menu the user already opens says one thing only.
 */
type ClearableProps = BaseProps & {
  allowClear: true;
  value: MaturityChoice | undefined | null;
  onChange: (value: MaturityOption, meta: ActionMeta<MaturityOption>) => void;
};

type LevelOnlyProps = BaseProps & {
  allowClear?: false;
  value: MaturityLevel | undefined | null;
  onChange: (value: MaturityLevelOption, meta: ActionMeta<MaturityLevelOption>) => void;
};

export type MaturitySelectProps = ClearableProps | LevelOnlyProps;

export const MaturitySelect: React.FC<MaturitySelectProps> = ({
  value,
  onChange,
  onBlur,
  error,
  helperText,
  disabled,
  allowClear = false,
  placeholder = 'Select maturity',
  ...rest
}) => {
  const options: MaturityOption[] = allowClear ? [maturityClearOption, ...maturityLevelOptions] : maturityLevelOptions;

  return (
    <>
      <CSelect
        isDisabled={disabled}
        /*
         * `?? null`, never a bare `undefined`. react-select's StateManager reads `undefined` as
         * "uncontrolled" and falls back to its OWN last selection, so a field whose value went
         * back to null kept rendering whatever was last picked — the cell showed "Clear" after
         * clearing, and a bulk clear left every row still showing its old score. Unreachable
         * until CRPD-7274 made a maturity able to become null again; `null` is the value that
         * means "controlled, nothing selected".
         */
        value={_.find(options, (o) => o.value === value) ?? null}
        placeholder={placeholder}
        options={options}
        onChange={onChange}
        onBlur={onBlur}
        {...rest}
      />
      {error && helperText && <Error>{helperText}</Error>}
    </>
  );
};
